import { useEffect, useState } from "react";
import { BarChart3, Loader2 } from "lucide-react";
import api from "@/lib/api";

const DIFF_TONE = {
  easy: "bg-moss",
  intermediate: "bg-cadmium",
  hard: "bg-amber",
  unrated: "bg-white/20",
};

export const CourseAnalytics = ({ courseId }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setData(null);
    setFailed(false);
    api.get(`/training/courses/${courseId}/analytics`)
      .then((res) => setData(res.data))
      .catch(() => setFailed(true));
  }, [courseId]);

  if (failed) return null;

  return (
    <div className="technical-card p-6 mt-4" data-testid={`course-analytics-${courseId}`}>
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 size={14} className="text-sky-600" />
        <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900">
          Exam Analytics {data ? `— ${data.title}` : ""}
        </p>
      </div>
      <p className="text-[11px] text-muted-foreground mb-5">
        Pass rates by question difficulty and the questions employees miss most, from scored exam attempts.
      </p>
      {!data ? (
        <div className="flex items-center justify-center py-8"><Loader2 size={18} className="animate-spin text-muted-foreground" /></div>
      ) : data.scored_attempts === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground" data-testid="analytics-empty">
          No scored attempts yet — analytics appear after employees take this exam.
        </p>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">
              Pass rate by difficulty · {data.scored_attempts} scored attempt(s)
            </p>
            <div className="space-y-3" data-testid="analytics-difficulty">
              {data.difficulty.map((d) => (
                <div key={d.difficulty} data-testid={`analytics-diff-${d.difficulty}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                      {d.difficulty} <span className="text-muted-foreground/50">({d.questions} Q)</span>
                    </span>
                    <span className="text-[11px] font-mono text-slate-900">
                      {d.pass_rate != null ? `${d.pass_rate}%` : "no attempts"}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${DIFF_TONE[d.difficulty] || DIFF_TONE.unrated}`}
                      style={{ width: `${d.pass_rate ?? 0}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Most missed questions</p>
            {data.most_missed.length === 0 ? (
              <p className="text-sm text-muted-foreground">No misses recorded.</p>
            ) : (
              <div className="space-y-2" data-testid="analytics-missed">
                {data.most_missed.map((m, i) => (
                  <div key={m.index} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5" data-testid={`analytics-missed-${i}`}>
                    <p className="text-xs text-slate-900 leading-snug">Q{m.index + 1}. {m.q}</p>
                    <p className="mt-1 text-[10px] font-mono text-muted-foreground">
                      <span className={m.miss_rate >= 50 ? "text-amber-700" : "text-green-700"}>{m.miss_rate}% missed</span>
                      {" "}· {m.attempts} attempt(s) · {m.difficulty} · {m.category}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
